export type SiteConsent = 'essential' | 'analytics';

export const SITE_CONSENT_STORAGE_KEY = 'untangled:consent:v1';
export const SITE_CONSENT_EVENT = 'untangled:consent-changed';

// A failed write must never leave an old persisted grant authoritative in this tab.
let storageFailed = false;
// Only distinguishes this document's own broadcasts; it is not a credential.
const broadcastSource = Math.random().toString(36).slice(2);

export function readSiteConsent(): SiteConsent | null {
  if (typeof window === 'undefined') return null;
  if (storageFailed) return null;
  try {
    const value = window.localStorage.getItem(SITE_CONSENT_STORAGE_KEY);
    return value === 'analytics' || value === 'essential' ? value : null;
  } catch {
    storageFailed = true;
    return null;
  }
}

export function writeSiteConsent(value: SiteConsent): boolean {
  if (typeof window === 'undefined') return false;
  let saved = false;
  try {
    window.localStorage.setItem(SITE_CONSENT_STORAGE_KEY, value);
    saved = window.localStorage.getItem(SITE_CONSENT_STORAGE_KEY) === value;
  } catch {
    // Quota failures can still permit removal: erase only this stale preference.
    try { window.localStorage.removeItem(SITE_CONSENT_STORAGE_KEY); } catch { /* Tab-local denial remains authoritative. */ }
  }
  storageFailed = !saved;
  if (!saved && typeof BroadcastChannel !== 'undefined') {
    // Propagate a withdrawal even when localStorage cannot be written.
    try {
      const channel = new BroadcastChannel(SITE_CONSENT_EVENT);
      channel.postMessage({ type: 'storage-failed', source: broadcastSource });
      channel.close();
    } catch { /* This tab still fails closed. */ }
  }
  window.dispatchEvent(new Event(SITE_CONSENT_EVENT));
  return saved;
}

export function subscribeSiteConsent(listener: () => void) {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === SITE_CONSENT_STORAGE_KEY) listener();
  };
  let channel: BroadcastChannel | undefined;
  try {
    channel = new BroadcastChannel(SITE_CONSENT_EVENT);
    channel.onmessage = (event) => {
      if (event.data?.type === 'storage-failed' && event.data.source !== broadcastSource) { storageFailed = true; listener(); }
    };
  } catch { /* Storage events remain the primary cross-tab mechanism. */ }
  window.addEventListener(SITE_CONSENT_EVENT, listener);
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', listener);
  window.addEventListener('pageshow', listener);
  return () => {
    channel?.close();
    window.removeEventListener(SITE_CONSENT_EVENT, listener);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', listener);
    window.removeEventListener('pageshow', listener);
  };
}

export function analyticsConsentGranted() {
  return readSiteConsent() === 'analytics';
}
